/**
 * « Son visage »: generate the agent's avatar from a short description and a style preset (a series of 4 candidates
 * to pick from, regenerate at will), import an image (re-encoded on the device first), or go back to the character
 * drawn from its name.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type { AgentSettings, AvatarStyle } from "../../shared/contracts";
import { MAIN_AGENT_ID } from "../../shared/contracts";
import { api, errorText, fileUrl } from "../lib/api";
import { AVATAR_STYLE_OPTIONS } from "../lib/format";
import { ImageError, squareImage } from "../lib/image";
import { invalidate, mutateResource } from "../lib/store";
import { AgentAvatarView } from "./avatar";
import { IconRefresh, IconSparkle } from "./icons";
import { Button, cx, Field, Notice, RadioCard, Spinner, Textarea } from "./ui";

type Busy = "generate" | "choose" | "upload" | "reset" | null;

function savedMain(settings: AgentSettings): void {
  mutateResource<AgentSettings>("settings", () => settings);
  invalidate("dashboard");
}

/**
 * `agentId`: "main" (the cached settings of the app) or one of « Mes agents », whose page passes `onSaved` (its own
 * settings never replace the main agent's in the cache).
 */
export function AvatarStudio({ settings, agentId = MAIN_AGENT_ID, onSaved }: { settings: AgentSettings; agentId?: string; onSaved?: (settings: AgentSettings) => void }) {
  const saved = (next: AgentSettings) => (onSaved ? onSaved(next) : savedMain(next));
  const [style, setStyle] = useState<AvatarStyle>(settings.avatar?.style ?? "3d");
  const [prompt, setPrompt] = useState(settings.avatar?.prompt ?? "");
  const [candidates, setCandidates] = useState<string[]>([]);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const choicesRef = useRef<HTMLFieldSetElement>(null);
  const ids = { prompt: useId(), file: useId() };
  const name = settings.agentName;

  // A new series shows up below the form: bring it into view (on a phone it would land below the fold).
  useEffect(() => {
    if (!candidates.length) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    choicesRef.current?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "nearest" });
  }, [candidates]);

  async function run(kind: Exclude<Busy, null>, work: () => Promise<void>) {
    if (busy) return;
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (err) {
      setError(err instanceof ImageError ? err.message : errorText(err));
    } finally {
      setBusy(null);
    }
  }

  function generate(e?: FormEvent) {
    e?.preventDefault();
    const text = prompt.trim();
    if (text.length < 3) {
      setError(`Décris ${name} en quelques mots (au moins 3 caractères).`);
      return;
    }
    void run("generate", async () => {
      const res = await api("POST /api/agents/:agentId/avatar/candidates", { params: { agentId }, body: { style, prompt: text } });
      setCandidates(res.candidates);
      setPicked(res.candidates[0] ?? null);
    });
  }

  function choose() {
    if (!picked) return;
    void run("choose", async () => {
      saved(await api("PUT /api/agents/:agentId/avatar", { params: { agentId }, body: { fileKey: picked } }));
      setCandidates([]);
      setPicked(null);
      setNotice(`${name} a un nouveau visage.`);
    });
  }

  function upload(file: File | undefined) {
    if (!file) return;
    void run("upload", async () => {
      const image = await squareImage(file);
      saved(await api("POST /api/agents/:agentId/avatar/upload", { params: { agentId }, body: { image } }));
      setCandidates([]);
      setPicked(null);
      setNotice("Image importée : c'est son nouveau visage.");
    });
    if (fileRef.current) fileRef.current.value = "";
  }

  function reset() {
    void run("reset", async () => {
      saved(await api("DELETE /api/agents/:agentId/avatar", { params: { agentId } }));
      setNotice(`${name} reprend son visage dessiné.`);
    });
  }

  const current = settings.avatar;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-4">
        <AgentAvatarView agent={{ name, avatar: current, character: settings.character }} size={72} />
        {/* The text keeps at least 12rem beside the face; the buttons go to their own line on a phone. */}
        <div className="min-w-0 flex-1 basis-48">
          <p className="text-[14px] font-medium">
            {current ? (current.source === "upload" ? "Une image que tu as importée" : "Un avatar généré") : settings.character ? "Le personnage que tu as composé" : "Un personnage dessiné d'après son nom"}
          </p>
          <p className="text-[13px] text-muted">
            {current?.prompt ? `« ${current.prompt} »` : "Donne-lui le visage que tu veux : décris-le, ou importe une image."}
          </p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <label
            htmlFor={ids.file}
            className={cx(
              "inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-[14px] border border-line-strong px-3.5 text-[14px] font-semibold transition hover:bg-sunken has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent sm:h-9 sm:rounded-xl sm:text-[13.5px]",
              busy && "pointer-events-none opacity-50",
            )}
          >
            {busy === "upload" ? <Spinner className="h-3.5 w-3.5" /> : null}
            Importer une image
            <input
              id={ids.file}
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
              className="sr-only"
              disabled={busy !== null}
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
          {current ? (
            <Button variant="ghost" size="sm" busy={busy === "reset"} disabled={busy !== null} onClick={reset}>
              Revenir au visage dessiné
            </Button>
          ) : null}
        </div>
      </div>

      <form onSubmit={generate} noValidate className="space-y-4">
        <fieldset>
          <legend className="mb-2 text-[13px] font-semibold text-muted">Style</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {AVATAR_STYLE_OPTIONS.map((o) => (
              <RadioCard key={o.value} name="avatarStyle" value={o.value} label={o.label} checked={style === o.value} onChange={() => setStyle(o.value)}>
                {o.description}
              </RadioCard>
            ))}
          </div>
        </fieldset>
        <Field
          label={`Décris ${name}`}
          htmlFor={ids.prompt}
          hint="Ex. « un petit renard roux souriant, avec des lunettes rondes ». Un personnage, pas une vraie personne."
        >
          <Textarea id={ids.prompt} value={prompt} maxLength={300} className="min-h-20" onChange={(e) => setPrompt(e.target.value)} />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" busy={busy === "generate"} disabled={busy !== null}>
            {candidates.length ? <IconRefresh width={16} height={16} /> : <IconSparkle width={16} height={16} />}
            {candidates.length ? "Régénérer" : "Générer des propositions"}
          </Button>
          <p className="text-[12px] text-subtle" role={busy === "generate" ? "status" : undefined}>
            {busy === "generate" ? `${name} se dessine… quelques secondes.` : "4 propositions, environ 0,003 $ comptés dans ton budget IA."}
          </p>
        </div>
      </form>

      {candidates.length ? (
        <fieldset ref={choicesRef} className="min-w-0 scroll-mt-24">
          <legend className="mb-2 text-[13px] font-semibold text-muted">Choisis son visage</legend>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {candidates.map((key, i) => (
              <label
                key={key}
                className={cx(
                  "relative block aspect-square overflow-hidden rounded-2xl border-2 bg-sunken transition",
                  picked === key ? "border-accent ring-4 ring-[var(--glow)]" : "border-transparent hover:border-line-strong",
                )}
              >
                <img src={fileUrl(key)} alt="" className="h-full w-full object-cover" />
                <input
                  type="radio"
                  className="absolute top-2 left-2 h-5 w-5 drop-shadow"
                  name="avatarCandidate"
                  value={key}
                  checked={picked === key}
                  onChange={() => setPicked(key)}
                  aria-label={`Proposition ${i + 1}`}
                />
              </label>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button busy={busy === "choose"} disabled={!picked || busy !== null} onClick={choose}>
              Utiliser cette proposition
            </Button>
          </div>
        </fieldset>
      ) : null}

      {notice ? (
        <Notice tone="success" role="status">
          {notice}
        </Notice>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </div>
  );
}
